/**
 * The asset Subtitles tab's drafts (1.9.0), kept above the tab so switching tabs keeps unsaved edits; keyed by asset
 * and track, with the selected track per asset (AssetPage survives moving between assets). It follows the
 * MetadataTab pattern: a base revision; server changes are adopted while nothing is dirty; with a dirty draft a newer
 * server revision is noted (`serverChange`: "Changed by X: Reload / Keep mine"); after Save the base comes from the
 * PUT response; "Keep mine" adopts details.currentRevision from the 409.
 *
 * Beside the drafts: the cues selected in each draft and the active edge (the nudge keys' edge), shared by the cue
 * list, the strip under the player and the keys.
 */
import { useSyncExternalStore } from 'react';
import type { Cue, TrackStyle } from '@prism/contracts';
import { createStore } from '../pages/create/playheadClock';
import type { CueEdge } from './types';

export interface SubtitleDraft {
  assetId: string;
  trackId: string;
  /** The revision the draft was made from. */
  baseRevision: number;
  cues: Cue[];
  style: TrackStyle;
  label: string;
  language: string;
  dirty: boolean;
  /** A newer revision saved by someone else while this draft was dirty. */
  serverChange: { revision: number; by: string | null } | null;
}

/** What the drafts are made from (the web SubtitleTrackDTO's fields). */
export interface DraftSource {
  id: string;
  revision: number;
  cues: Cue[];
  style?: TrackStyle | null;
  label: string;
  language: string;
  updatedBy?: string | null;
}

export const draftKey = (assetId: string, trackId: string): string => `${assetId}:${trackId}`;

const drafts = createStore<ReadonlyMap<string, SubtitleDraft>>(new Map());
const selected = createStore<ReadonlyMap<string, string | null>>(new Map());

export const getDraft = (assetId: string, trackId: string): SubtitleDraft | null => drafts.get().get(draftKey(assetId, trackId)) ?? null;

export function setDraft(draft: SubtitleDraft): void {
  const key = draftKey(draft.assetId, draft.trackId);
  if (drafts.get().get(key) === draft) return;
  const next = new Map(drafts.get());
  next.set(key, draft);
  drafts.set(next);
}

export function discardDraft(assetId: string, trackId: string): void {
  if (!drafts.get().has(draftKey(assetId, trackId))) return;
  const next = new Map(drafts.get());
  next.delete(draftKey(assetId, trackId));
  drafts.set(next);
}

/** Whether the asset has a draft with unsaved edits (the leave guard asks first). */
export const hasUnsavedDrafts = (assetId: string): boolean => [...drafts.get().values()].some((d) => d.assetId === assetId && d.dirty);

export const selectedTrack = (assetId: string): string | null => selected.get().get(assetId) ?? null;
export function selectTrack(assetId: string, trackId: string | null): void {
  if (selected.get().get(assetId) === trackId) return;
  const next = new Map(selected.get());
  next.set(assetId, trackId);
  selected.set(next);
}

/* ---------------------------------------------------------------- the merge rules (pure) */

/** A clean draft of the track as saved. */
export function draftFrom(assetId: string, t: DraftSource): SubtitleDraft {
  return { assetId, trackId: t.id, baseRevision: t.revision, cues: t.cues, style: t.style ?? {}, label: t.label, language: t.language, dirty: false, serverChange: null };
}

/**
 * The draft for the track as the server now has it: none yet → a clean draft; the same revision → kept; a newer
 * revision → adopted while nothing is dirty, else kept and the change noted (by whom); an older one (a list fetched
 * before our own save) → kept.
 */
export function reconcileDraft(draft: SubtitleDraft | null, assetId: string, t: DraftSource): SubtitleDraft {
  if (!draft) return draftFrom(assetId, t);
  if (t.revision <= draft.baseRevision) return draft;
  if (!draft.dirty) return draftFrom(assetId, t);
  if (draft.serverChange?.revision === t.revision) return draft;
  return { ...draft, serverChange: { revision: t.revision, by: t.updatedBy ?? null } };
}

/** An edit: the draft with new cues, style, label or language, now dirty. The same draft when nothing changes. */
export function editDraft(draft: SubtitleDraft, patch: Partial<Pick<SubtitleDraft, 'cues' | 'style' | 'label' | 'language'>>): SubtitleDraft {
  const changed = (Object.keys(patch) as Array<keyof typeof patch>).some((k) => patch[k] !== draft[k]);
  return changed ? { ...draft, ...patch, dirty: true } : draft;
}

/** After Save: the base and content from the PUT response, clean. */
export const savedDraft = (draft: SubtitleDraft, t: DraftSource): SubtitleDraft => draftFrom(draft.assetId, t);

/** "Keep mine" after a 409 or a noted change: the draft now builds on `currentRevision`; still dirty. */
export const keepMine = (draft: SubtitleDraft, currentRevision: number): SubtitleDraft =>
  ({ ...draft, baseRevision: Math.max(draft.baseRevision, currentRevision), serverChange: null, dirty: true });

/* ---------------------------------------------------------------- cue selection and the active edge */

export interface CueSelection { ids: string[]; edge: CueEdge | null }
const NONE: CueSelection = Object.freeze({ ids: [], edge: null }) as CueSelection;
const selections = createStore<ReadonlyMap<string, CueSelection>>(new Map());

export const getCueSelection = (assetId: string, trackId: string): CueSelection => selections.get().get(draftKey(assetId, trackId)) ?? NONE;
export function setCueSelection(assetId: string, trackId: string, sel: CueSelection): void {
  const key = draftKey(assetId, trackId);
  const cur = selections.get().get(key);
  if (cur && cur.edge === sel.edge && cur.ids.length === sel.ids.length && cur.ids.every((id, i) => id === sel.ids[i])) return;
  const next = new Map(selections.get());
  next.set(key, sel);
  selections.set(next);
}

/** A click on a cue: it alone, or (extend) added to or taken out of the selection. */
export function clickSelection(sel: CueSelection, id: string, extend: boolean): CueSelection {
  if (!extend) return { ids: [id], edge: sel.ids.length === 1 && sel.ids[0] === id ? sel.edge : null };
  return sel.ids.includes(id) ? { ids: sel.ids.filter((x) => x !== id), edge: null } : { ids: [...sel.ids, id], edge: null };
}

/* ---------------------------------------------------------------- hooks */

let version = 0;
const bump = () => { version++; };
drafts.subscribe(bump);
selected.subscribe(bump);
selections.subscribe(bump);

/** Subscribe to every change (drafts, the selected track, the cue selection); for useSyncExternalStore. */
export function subscribeDrafts(fn: () => void): () => void {
  const a = drafts.subscribe(fn), b = selected.subscribe(fn), c = selections.subscribe(fn);
  return () => { a(); b(); c(); };
}

/** Re-renders on every change of the store. */
export function useDraftStore(): number {
  return useSyncExternalStore(subscribeDrafts, () => version, () => version);
}
